import { supabase } from '../lib/supabaseClient'
import { PLAN_PRICES_USD } from '../data/pricingPlans'

/**
 * 📢 PRODUCTION-READY MIXPANEL TRACKER
 * Tracks events via Supabase Edge Function with automatic JWT authentication.
 *
 * All calls use supabase.functions.invoke() which automatically attaches the user's JWT.
 * For anonymous events (before login), pass user_id: null.
 */

export async function trackCriticalEvent(
  eventName: string,
  properties: Record<string, any> = {},
): Promise<void> {
  try {
    // Get authenticated user (null if not logged in)
    const {
      data: { user },
    } = await supabase.auth.getUser()

    const userId = user?.id || null

    console.log(`📊 Tracking event: ${eventName}`, { userId, properties })

    // Invoke Supabase Edge Function (automatically includes JWT)
    const response = await supabase.functions.invoke('track-mixpanel', {
      body: {
        event: eventName,
        user_id: userId,
        properties: {
          ...properties,
          client_timestamp: new Date().toISOString(),
        },
      },
    })

    if (response.error) {
      console.error(
        `❌ Mixpanel tracking error for ${eventName}:`,
        response.error,
      )
    } else {
      console.log(`✅ Event tracked successfully: ${eventName}`)
    }
  } catch (error) {
    // Silent fail - don't crash the app if tracking fails
    console.error(`🌐 Mixpanel tracking exception for ${eventName}:`, error)
  }
}

// ====================================================================
// 🚀 HELPER FUNCTIONS FOR SPECIFIC EVENTS
// ====================================================================

/**
 * Track Single Scan Purchase (list price in USD)
 */
export async function trackSingleScanPurchase(
  toxicScore: number,
  primaryPattern: string,
  isFirstTimeBuyer: boolean = true,
): Promise<void> {
  await trackCriticalEvent('Single_Scan_Purchased', {
    Toxic_Score: toxicScore,
    Primary_Pattern: primaryPattern,
    Price_Paid: PLAN_PRICES_USD.single_scan,
    Currency: 'USD',
    Is_First_Time_Buyer: isFirstTimeBuyer,
  })
}

/**
 * Track Share Action (Virality Loop)
 */
export async function trackShare(
  sharePlatform: string,
  scoreShared: number,
  userPlanType: string,
): Promise<void> {
  await trackCriticalEvent('Shared_Via', {
    Share_Platform: sharePlatform,
    Score_Shared: scoreShared,
    User_Plan_Type: userPlanType,
  })
}

/** Subscription list price (USD) per plan type, from the pricing data. */
const SUBSCRIPTION_PRICE: Record<string, number> = {
  weekly: PLAN_PRICES_USD.weekly,
  monthly: PLAN_PRICES_USD.monthly,
  yearly: PLAN_PRICES_USD.yearly,
}
/**
 * Track Subscription Purchase. Pass the checkout plan type
 * ('weekly' | 'monthly' | 'yearly') so the recorded price matches the plan.
 */
export async function trackSubscriptionPurchase(
  planType: string = 'monthly',
  wasUpgrade: boolean = false,
  daysSinceFirstScan: number = 0,
): Promise<void> {
  await trackCriticalEvent('Subscription_Purchased', {
    Plan_Type: planType,
    Price_Paid: SUBSCRIPTION_PRICE[planType] ?? null,
    Currency: 'USD',
    Was_Upgrade: wasUpgrade,
    Days_Since_First_Scan: daysSinceFirstScan,
  })
}
